import { Suspense, lazy, useEffect, useState } from "react";
import { HashRouter, Navigate, Route, Routes } from "react-router-dom";
import type { Me } from "./api";
import { useAuth } from "./auth";
import { AdminPage } from "./pages/Admin";
import { BookPage } from "./pages/Book";
import { NoticePage, TermsPage } from "./pages/Legal";
import { LibraryPage, PublicBooksPage } from "./pages/Library";
import { LookupPage } from "./pages/Lookup";
import { PrivacyPage } from "./pages/Privacy";
import { Welcome } from "./pages/Welcome";
import { StudioPage } from "./studio/StudioPage";
import {
  ArticlePage,
  CategoryPage,
  ChangesPage,
  ChapterPageView,
  PortalPage,
  SearchPage,
  TimelinePage,
} from "./wiki/pages";
import { WikiLayout } from "./wiki/WikiLayout";

const GraphPage = lazy(() => import("./wiki/GraphPage").then((m) => ({ default: m.GraphPage })));
import { ErrorNote, Spinner, useLoad } from "./ui";

// Pages that work without logging in (linked from the infosite footer).
const PUBLIC_PAGES: Record<string, () => React.ReactElement> = {
  "#/melden": () => <NoticePage />,
  "#/voorwaarden": () => <TermsPage />,
  "#/privacy": () => <PrivacyPage />,
};

function useHash(): string {
  const [hash, setHash] = useState(window.location.hash);
  useEffect(() => {
    const on = () => setHash(window.location.hash);
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return hash;
}

export function App() {
  const auth = useAuth();
  const hash = useHash();
  const publicPage = PUBLIC_PAGES[hash.split("?")[0]];
  if (publicPage && !auth.loggedIn && (auth.config || auth.ready)) {
    return <Shell>{publicPage()}</Shell>;
  }
  if (!auth.ready) return <Shell><Spinner /></Shell>;
  if (auth.error) return <Shell><ErrorNote error={auth.error} /></Shell>;
  if (!auth.loggedIn) return <Shell><Welcome /></Shell>;
  return <SignedIn />;
}

function Shell({ children, me }: { children: React.ReactNode; me?: Me | null }) {
  const { loggedIn, logout } = useAuth();
  const section = useHash().startsWith("#/admin") ? "admin" : "books";
  return (
    <div className="app">
      <header className="topbar">
        <a className="brand" href="#/" aria-label="FabulaWiki">
          Fabula<b>Wiki</b>
        </a>
        <nav className="topnav" aria-label="Hoofdmenu">
          {me && (
            <a href="#/" className={section === "books" ? "active" : ""}>
              Boeken
            </a>
          )}
          {me?.is_admin && (
            <a href="#/admin" className={section === "admin" ? "active" : ""}>
              Beheer
            </a>
          )}
        </nav>
        {me && (
          <span className="credits" title="Een mislukte of geannuleerde verwerking geeft de credit terug.">
            <b>{me.credits_remaining}</b> credit{me.credits_remaining === 1 ? "" : "s"}
          </span>
        )}
        {loggedIn && (
          <button className="btn btn-ghost small" onClick={logout}>
            Uitloggen
          </button>
        )}
      </header>
      <main className="content">{children}</main>
      <footer className="footer muted small">
        <nav className="footer-links">
          <a href="#/voorwaarden">Voorwaarden</a> · <a href="#/privacy">Privacy</a> ·{" "}
          <a href="#/melden">Inbreuk melden</a>
        </nav>
        <p>
          Alleen voor rechtmatig verkregen boeken voor privégebruik, boeken met toestemming van de
          rechthebbende, of publiek domein. FabulaWiki heeft geen relatie met auteurs of
          uitgevers.
        </p>
      </footer>
    </div>
  );
}

function SignedIn() {
  const { api } = useAuth();
  const me = useLoad(() => api.me(), [api]);
  if (me.loading && !me.data) return <Shell><Spinner /></Shell>;
  if (me.error || !me.data) return <Shell><ErrorNote error={me.error ?? "onbekende fout"} /></Shell>;
  // Pending accounts (no credits yet) use the app like anyone else: reading public books and
  // the wiki only needs a login; the library shows "awaiting approval" instead of the upload.
  if (me.data.status === "blocked")
    return <Shell><ErrorNote error="Dit account is geblokkeerd." /></Shell>;
  return (
    <HashRouter>
      <Shell me={me.data}>
        <Routes>
          <Route path="/" element={<LibraryPage me={me.data} onChange={me.reload} />} />
          <Route path="/openbaar" element={<PublicBooksPage />} />
          <Route path="/books/:bookId" element={<BookPage me={me.data} />} />
          <Route path="/books/:bookId/opzoeken" element={<LookupPage />} />
          <Route path="/books/:bookId/wiki" element={<WikiLayout me={me.data} />}>
            <Route index element={<PortalPage />} />
            <Route path="a/:entityId" element={<ArticlePage />} />
            <Route path="h/:chapter" element={<ChapterPageView />} />
            <Route path="c/:type" element={<CategoryPage />} />
            <Route path="tijdlijn" element={<TimelinePage />} />
            <Route
              path="kaart"
              element={
                <Suspense fallback={<Spinner />}>
                  <GraphPage />
                </Suspense>
              }
            />
            <Route path="nieuw" element={<ChangesPage />} />
            <Route path="zoeken" element={<SearchPage />} />
          </Route>
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/voorwaarden" element={<TermsPage />} />
          <Route path="/melden" element={<NoticePage />} />
          <Route path="/books/:bookId/beheer" element={<StudioPage me={me.data} />} />
          {me.data.is_admin && <Route path="/admin" element={<AdminPage />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
    </HashRouter>
  );
}
